import { useMemo, useEffect } from 'react'
import { useStore } from '@w/react-store'
import type { QueryClient, QueryData } from './query-client'
import { useQueryClient } from './query-client-provider'
import type { UseQueryOptions } from './use-query-types'
import type { ReactPromise } from '@w/utils-fe'

export type { UseQueryOptions } from './use-query-types'

export interface UseQueryResult<TData = unknown> {
  data: TData | undefined
  error: Error | undefined
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  isPending: boolean
  isRefetching: boolean
  isSuccess: boolean
  refetch: () => ReactPromise<QueryData>
  status: 'pending' | 'error' | 'success'
  promise: ReactPromise<QueryData> | null
}

export function useQuery<TQueryFnData = unknown, TData = TQueryFnData>(
  options: UseQueryOptions<TQueryFnData, TData>,
  queryClient?: QueryClient,
): UseQueryResult<TData> {
  const client = useQueryClient(queryClient)

  const { select, ...queryOptions } = options

  // Sync query options and get query (runs on every render)
  const query = client.syncQueryOptions(queryOptions)

  // Subscribe to query state via dedicated store
  const state = useStore(query.getStore())

  // Track mount/unmount for auto-fetch and GC logic
  useEffect(() => {
    return query.subscribe(() => {
      // No-op callback - we only need this for mount/unmount tracking
    })
  }, [query])

  // Apply selector with caching to preserve referential equality
  // Cache by data reference only - ignore select function identity changes
  // This preserves referential equality when data hasn't changed
  const data = useMemo(() => {
    return select && state.data !== undefined
      ? select(state.data as TQueryFnData)
      : (state.data as TData | undefined)
  }, [select, state.data])

  const isPending = state.status === 'pending'
  const isSuccess = state.status === 'success'
  const isError = state.status === 'error'
  const isFetching = state.fetchStatus === 'fetching'
  const isLoading = isPending && isFetching
  const isRefetching = isSuccess && isFetching

  return {
    data,
    error: state.error,
    isError,
    isFetching,
    isLoading,
    isPending,
    isRefetching,
    isSuccess,
    refetch: query.fetch,
    status: state.status,
    promise: state.promise,
  }
}
